import React, { useState, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';
import { CCard, CCardBody, CCardHeader, CCol, CButton, CRow, CBadge } from '@coreui/react';
import Loading from '../../../components/Loading';
import Error from '../../../components/Error';
import PageLayout from '../../../components/PageLayout';
import PaginationTable from '../../../components/PaginationTable';
import ConfirmationModal from '../../../components/ConfirmationModal';
import { getAllDiscountCodes, deleteDiscountCode } from '../../../services/BackendService';
import { IDiscountCode } from './types';
import _t from 'counterpart';
import toast from 'react-hot-toast';
import { extractErrorMessage } from '../../../helpers';
import { useAppSelector } from '../../../helpers/customHooks';
import { Permission } from '../../../reducers/userReducer';

const DiscountCodesPage = () => {
  const [deleteModalShow, setDeleteModalShow] = useState<boolean>(false);
  const [discountCodeToDelete, setDiscountCodeToDelete] = useState<IDiscountCode | null>(null);

  const permissions = useAppSelector((state) => state.user.permissions);
  const history = useHistory();
  const queryClient = useQueryClient();

  const {
    data: discountCodes,
    isLoading,
    isError,
    refetch,
  } = useQuery<IDiscountCode[]>('discount-codes', getAllDiscountCodes, {
    onError: (e: any) => {
      const error = extractErrorMessage(e);
      toast.error(error);
    },
  });

  const deleteDiscountCodeMutation = useMutation((id: string) => deleteDiscountCode(id), {
    onSuccess: async () => {
      await queryClient.invalidateQueries('discount-codes');
      toast.success(_t('discount-codes.discount-code-deleted-successfully'));
      setDeleteModalShow(false);
      setDiscountCodeToDelete(null);
    },
    onError: (e: any) => {
      const error = extractErrorMessage(e);
      toast.error(error);
    },
    retry: false,
  });

  const onAddClicked = () => {
    history.push('/discount-codes/form');
  };

  const onEditClicked = (id: string) => {
    history.push(`/discount-codes/form?edit=${id}`);
  };

  const onDeleteClicked = (discountCode: IDiscountCode) => {
    setDiscountCodeToDelete(discountCode);
    setDeleteModalShow(true);
  };

  const deleteConfirmed = () => {
    if (discountCodeToDelete) {
      deleteDiscountCodeMutation.mutate(discountCodeToDelete.id);
    }
  };

  const formatDiscountValue = (discountCode: IDiscountCode) => {
    if (discountCode.fixedDiscountAmount) {
      const amount = Number(discountCode.fixedDiscountAmount);
      return `$${amount.toFixed(2)}`;
    } else if (discountCode.percentageDiscount) {
      const percentage = Number(discountCode.percentageDiscount);
      return `${percentage}%`;
    }
    return '-';
  };

  const formatDate = (date: Date | null) => {
    if (!date) return _t('discount-codes.unlimited');
    return new Date(date).toLocaleDateString();
  };

  const hasPermission = (permission: Permission) => {
    return permissions?.includes(permission);
  };

  const tableFields = useMemo(
    () => [
      { key: 'name', label: _t('global.name'), sorter: false },
      { key: 'code', label: _t('discount-codes.code'), sorter: false },
      { key: 'discount', label: _t('discount-codes.discount'), sorter: false },
      { key: 'startDate', label: _t('discount-codes.valid-from'), sorter: false },
      { key: 'endDate', label: _t('discount-codes.valid-until'), sorter: false },
      { key: 'maxUsesPerUser', label: _t('discount-codes.max-uses-per-user'), sorter: false },
      { key: 'isActive', label: _t('global.status'), sorter: false },
    ],
    []
  );

  const scopedSlots = useMemo(
    () => ({
      name: ({ name }: IDiscountCode) => <td>{name || '-'}</td>,
      code: ({ code }: IDiscountCode) => (
        <td>
          <code>{code}</code>
        </td>
      ),
      discount: ({ fixedDiscountAmount, percentageDiscount }: IDiscountCode) => (
        <td>{formatDiscountValue({ fixedDiscountAmount, percentageDiscount } as IDiscountCode)}</td>
      ),
      startDate: ({ startDate }: IDiscountCode) => <td>{formatDate(startDate)}</td>,
      endDate: ({ endDate }: IDiscountCode) => <td>{formatDate(endDate)}</td>,
      maxUsesPerUser: ({ maxUsesPerUser }: IDiscountCode) => (
        <td>{maxUsesPerUser || _t('discount-codes.unlimited')}</td>
      ),
      isActive: ({ isActive }: IDiscountCode) => (
        <td>
          <CBadge color={isActive ? 'success' : 'danger'}>
            {isActive ? _t('global.active') : _t('global.inactive')}
          </CBadge>
        </td>
      ),
    }),
    [hasPermission]
  );

  const rowClicked = useCallback(
    (discountCode: IDiscountCode) => {
      const { id } = discountCode;
      history.push(`/discount-codes/${id}`);
    },
    [history]
  );

  const onErrorRetry = () => {
    refetch();
  };

  if (isLoading) {
    return <Loading />;
  }

  if (isError || !discountCodes) {
    return <Error onRetry={onErrorRetry} />;
  }

  if (!Array.isArray(discountCodes)) {
    return <Error onRetry={onErrorRetry} />;
  }

  return (
    <PageLayout title={_t('discount-codes.title')}>
      <CRow>
        <CCol md={12}>
          <CCard>
            <CCardHeader className="pb-0">
              <div className="float-right">
                {hasPermission(Permission.MANAGE_PROP_FUNNELS) && (
                  <CButton className="mr-2 header-button-add" onClick={onAddClicked} />
                )}
              </div>
            </CCardHeader>
            <CCardBody>
              <PaginationTable
                tableFields={tableFields}
                scopedSlots={scopedSlots}
                data={Array.isArray(discountCodes) ? discountCodes : []}
                loading={isLoading}
                pages={0}
                pagination={false}
                onRowClicked={rowClicked}
                clickableRows
              />
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      <ConfirmationModal
        id="delete-discount-code"
        show={deleteModalShow}
        hidePanel={() => setDeleteModalShow(false)}
        onConfirm={() => deleteConfirmed()}
        title={_t('discount-codes.delete-modal-title')}
        error={null}
        buttonColor="danger"
        withMutation={false}
      />
    </PageLayout>
  );
};

export default DiscountCodesPage;
